import * as React from 'react';
import { useMemo } from 'react';
import { useStyletron } from 'baseui';
import { Button, KIND, SHAPE, SIZE } from 'baseui/button';
import { ARTWORK_SIZES, ListItem } from 'baseui/list';
import useAnalyticsForReviewSuggestions from './analytics/useAnalyticsForReviewSuggestions';
import { REVIEW_SUGGESTIONS_CONFIG } from './config';
import { ListItemOverrides, SuggestionContainer, UpdateDismissContainer } from './styled-components-overrides';
import type { ReviewSuggestionsDetailT, SuggestionActionT } from './types';
import { SUGGESTION_ACTION, SUGGESTION_CATEGORY, SUGGESTION_STATE } from './types';
type PropsT = {
  error: ReviewSuggestionsDetailT;
  onSuggestionAction: (error: ReviewSuggestionsDetailT, action: SuggestionActionT) => Promise<void>;
};
export const ReviewSuggestionsList = ({
  error,
  onSuggestionAction
}: PropsT) => {
  const [, theme] = useStyletron();
  const {
    sendEventForAcceptMLSuggestion,
    sendEventForRejectMLSuggestion
  } = useAnalyticsForReviewSuggestions();
  async function handleDismiss(error: ReviewSuggestionsDetailT) {
    switch (error.category) {
      case SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR:
      case SUGGESTION_CATEGORY.SOP_RECOMMENDATIONS:
        sendEventForRejectMLSuggestion({
          error: error.answer.slice(error.suggestion.positionStart, error.suggestion.positionEnd + 1),
          suggestion: error.suggestion.suggestion,
          startIndex: error.suggestion.positionStart,
          endIndex: error.suggestion.positionEnd,
          automationRequestId: error.automationRequestId,
          originalAnswer: error.answer,
          questionnaireId: error.questionnaireId,
          questionId: error.questionId,
          objectId: error.questionId,
          suggestionId: error.suggestionIndex
        });
        break;
      case SUGGESTION_CATEGORY.TRACKING_INCONSISTENCY:
        sendEventForRejectMLSuggestion({
          objectId: error.annotationId,
          startIndex: error.startIndex,
          endIndex: error.endIndex,
          suggestionId: error.id
        });
        break;
    }
    const updatedError = {
      ...error,
      state: SUGGESTION_STATE.DISMISSED
    };
    await onSuggestionAction(updatedError, SUGGESTION_ACTION.DISMISS);
  }
  async function handleUpdate(error: ReviewSuggestionsDetailT) {
    switch (error.category) {
      case SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR:
      case SUGGESTION_CATEGORY.SOP_RECOMMENDATIONS:
        sendEventForAcceptMLSuggestion({
          error: error.answer.slice(error.suggestion.positionStart, error.suggestion.positionEnd + 1),
          suggestion: error.suggestion.suggestion,
          startIndex: error.suggestion.positionStart,
          endIndex: error.suggestion.positionEnd,
          automationRequestId: error.automationRequestId,
          originalAnswer: error.answer,
          questionnaireId: error.questionnaireId,
          questionId: error.questionId,
          objectId: error.questionId,
          suggestionId: error.suggestionIndex
        });
        break;
      case SUGGESTION_CATEGORY.TRACKING_INCONSISTENCY:
        sendEventForAcceptMLSuggestion({
          objectId: error.annotationId,
          startIndex: error.startIndex,
          endIndex: error.endIndex,
          suggestionId: error.id
        });
        break;
    }
    const updatedError = {
      ...error,
      state: SUGGESTION_STATE.APPLIED
    };
    await onSuggestionAction(updatedError, SUGGESTION_ACTION.ACCEPT);
  }
  const modificationDetails = useMemo(() => {
    switch (error.category) {
      case SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR:
      case SUGGESTION_CATEGORY.SOP_RECOMMENDATIONS:
        return REVIEW_SUGGESTIONS_CONFIG[error.category](error, theme);
      case SUGGESTION_CATEGORY.TRACKING_INCONSISTENCY:
        return REVIEW_SUGGESTIONS_CONFIG[error.category](error, theme);
      default:
        return null;
    }
  }, [error, theme]);
  return <ListItem key={error.id} artworkSize={ARTWORK_SIZES.LARGE} overrides={ListItemOverrides}>
      <SuggestionContainer>{modificationDetails}</SuggestionContainer>
      <UpdateDismissContainer>
        <Button onClick={handleDismiss.bind(null, error)} size={SIZE.compact} shape={SHAPE.pill} kind={KIND.tertiary}>
          Dismiss
        </Button>
        <Button onClick={handleUpdate.bind(null, error)} size={SIZE.compact} shape={SHAPE.pill}>
          {error.category === SUGGESTION_CATEGORY.SPELLING_AND_GRAMMAR ? 'Accept' : <span>I&#x2019;ll fix it</span>}
        </Button>
      </UpdateDismissContainer>
    </ListItem>;
};